import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Church, HandCoins, BookOpen, LogOut } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { ArchWindow } from "../components/ArchWindow";
import { VersesTab } from "../components/admin/VersesTab";

const STATUS_COLORS = {
  new: "bg-[#E84C4C]/10 text-[#E84C4C] border-[#E84C4C]/30",
  in_review: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  accepted: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  completed: "bg-sky-500/10 text-sky-600 border-sky-500/30",
  declined: "bg-neutral-500/10 text-neutral-500 border-neutral-400/40",
};
const STATUSES = ["new", "in_review", "accepted", "completed", "declined"];
const inputCls = "w-full rounded-lg bg-white border border-[#120716]/15 px-4 py-3 text-sm text-[#120716] placeholder:text-[#120716]/35 focus:outline-none focus:border-[#E84C4C] transition-colors";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF5F2] flex items-center justify-center px-6">
      <form onSubmit={submit} className="glass rounded-2xl p-10 w-full max-w-md" data-testid="admin-login-form">
        <div className="flex justify-center"><ArchWindow size={34} /></div>
        <h1 className="font-orbitron font-bold text-xl text-[#120716] text-center mt-6 tracking-widest">MISSION CONTROL</h1>
        <p className="text-center text-xs text-[#120716]/50 mt-2">Admin access for Luke Adams</p>
        <div className="mt-8 space-y-4">
          <input data-testid="admin-email-input" type="email" required className={inputCls} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="admin-password-input" type="password" required className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {error && <p data-testid="admin-login-error" className="mt-4 text-sm text-[#E84C4C]">{error}</p>}
        <button data-testid="admin-login-submit" disabled={loading} className="mt-6 w-full rounded-full bg-[#E84C4C] hover:bg-[#f05e5e] disabled:opacity-50 text-white font-semibold py-3.5 transition-all">
          {loading ? "Signing in..." : "Enter Dashboard"}
        </button>
        <Link to="/" className="block text-center mt-6 text-xs text-[#120716]/50 hover:text-[#E84C4C]">← Back to site</Link>
      </form>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("applications");
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [donations, setDonations] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [s, a, d] = await Promise.all([
        api.get("/admin/stats"), api.get("/applications"), api.get("/donations"),
      ]);
      setStats(s.data); setApps(a.data); setDonations(d.data);
    } catch (e) {
      toast.error("Failed to load dashboard data");
    }
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { setUser(r.data); })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/applications/${id}/status`, { status });
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success(`Status updated to ${status.replace("_", " ")}`);
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  if (checking) return <div className="min-h-screen bg-[#FFF5F2] flex items-center justify-center text-[#E84C4C] font-orbitron tracking-widest">LOADING...</div>;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-[#FFF5F2] flex" data-testid="admin-dashboard">
      <aside className="w-60 shrink-0 hidden md:flex flex-col border-r border-[#120716]/10 bg-white p-6">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <ArchWindow size={22} />
          <span className="font-orbitron font-black text-[#120716] text-sm tracking-widest">DIGITAL<br /><span className="text-[10px] tracking-[0.4em] text-[#E84C4C] font-medium">MISSIONS</span></span>
        </Link>
        {[["applications", "Applications", Church], ["donations", "Donations", HandCoins], ["verses", "Verse of the Day", BookOpen]].map(([k, l, Icon]) => (
          <button
            key={k}
            data-testid={`admin-tab-${k}`}
            onClick={() => setTab(k)}
            className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm mb-2 transition-colors ${tab === k ? "bg-[#E84C4C]/10 text-[#E84C4C] border border-[#E84C4C]/30" : "text-[#120716]/60 hover:text-[#120716]"}`}
          >
            <Icon className="w-4 h-4" /> {l}
          </button>
        ))}
        <button data-testid="admin-logout-btn" onClick={logout} className="mt-auto flex items-center gap-3 rounded-lg px-4 py-3 text-sm text-[#120716]/60 hover:text-[#E84C4C] transition-colors">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </aside>

      <main className="flex-1 p-6 md:p-10 overflow-x-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-orbitron font-bold text-xl md:text-2xl text-[#120716] tracking-wider flex items-center gap-3">
              <LayoutDashboard className="w-6 h-6 text-[#E84C4C]" /> MISSION CONTROL
            </h1>
            <p className="text-xs text-[#120716]/50 mt-1">Welcome back, {user.name || user.email} · "Well done, good and faithful servant." — Matthew 25:23</p>
          </div>
          <button onClick={logout} className="md:hidden text-[#E84C4C]" data-testid="admin-logout-mobile"><LogOut className="w-5 h-5" /></button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
            {[
              ["Applications", stats.total_applications],
              ["New / Pending", stats.new_applications],
              ["Raised", `$${stats.total_raised.toLocaleString()}`],
              ["Gifts", stats.donation_count],
            ].map(([l, v]) => (
              <div key={l} className="rounded-xl bg-white border border-[#120716]/10 shadow-sm p-5">
                <div className="font-orbitron font-bold text-2xl text-[#120716]">{v}</div>
                <div className="text-xs tracking-widest uppercase text-[#120716]/50 mt-1">{l}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "applications" && (
          <div className="rounded-xl bg-white border border-[#120716]/10 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-[#120716]/10 font-orbitron text-sm tracking-widest text-[#120716]/70">CHURCH APPLICATIONS</div>
            {apps.length === 0 ? (
              <p className="p-8 text-sm text-[#120716]/50" data-testid="no-applications">No applications yet. They will appear here when churches apply.</p>
            ) : (
              <div className="divide-y divide-[#120716]/10">
                {apps.map((a) => (
                  <div key={a.id} className="p-6" data-testid={`application-row-${a.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-[#120716]">{a.church_name}</h3>
                        <p className="text-xs text-[#120716]/55 mt-1">{a.pastor_name} · {a.email} · {a.location}{a.member_count ? ` · ${a.member_count} members` : ""}</p>
                        {a.needs?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            {a.needs.map((n) => (
                              <span key={n} className="rounded-full bg-[#E84C4C]/10 border border-[#E84C4C]/25 text-[#E84C4C] text-[10px] px-2.5 py-1">{n}</span>
                            ))}
                          </div>
                        )}
                        <p className="mt-3 text-sm text-[#120716]/70 max-w-2xl leading-relaxed">{a.vision}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`rounded-full border px-3 py-1 text-[10px] font-orbitron tracking-widest uppercase ${STATUS_COLORS[a.status] || STATUS_COLORS.new}`}>
                          {(a.status || "new").replace("_", " ")}
                        </span>
                        <select
                          data-testid={`application-status-select-${a.id}`}
                          value={a.status}
                          onChange={(e) => updateStatus(a.id, e.target.value)}
                          className="rounded-lg bg-white border border-[#120716]/15 px-3 py-2 text-xs text-[#120716] focus:outline-none focus:border-[#E84C4C]"
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "donations" && (
          <div className="rounded-xl bg-white border border-[#120716]/10 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-[#120716]/10 font-orbitron text-sm tracking-widest text-[#120716]/70">DONATION RECORDS</div>
            {donations.length === 0 ? (
              <p className="p-8 text-sm text-[#120716]/50" data-testid="no-donations">No donations recorded yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-orbitron tracking-widest text-[#120716]/50 uppercase">
                    <th className="px-6 py-3">Date</th>
                    <th className="px-6 py-3">Amount</th>
                    <th className="px-6 py-3">Type</th>
                    <th className="px-6 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#120716]/10">
                  {donations.map((d) => (
                    <tr key={d.session_id} data-testid={`donation-row-${d.session_id}`}>
                      <td className="px-6 py-4 text-[#120716]/60">{d.created_at ? new Date(d.created_at).toLocaleString() : "—"}</td>
                      <td className="px-6 py-4 font-orbitron text-[#120716]">${(d.amount / 100).toFixed(2)}</td>
                      <td className="px-6 py-4 text-[#120716]/60">{d.frequency === "monthly" ? "Monthly" : "One-time"}</td>
                      <td className="px-6 py-4">
                        <span className={`rounded-full border px-3 py-1 text-[10px] font-orbitron tracking-widest uppercase ${d.payment_status === "paid" ? STATUS_COLORS.accepted : STATUS_COLORS.declined}`}>
                          {d.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {tab === "verses" && <VersesTab />}
      </main>
    </div>
  );
}
